import { useCallback, useEffect, useRef } from 'react';
import {
  usePostChatDocumentBulkCreateMutation,
  usePostChatDocumentBulkGetUploadMutation,
  usePostChatDocumentBulkUploadCompleteMutation,
} from '@/api';
import { usePostChatDocumentDeleteMutation } from '@/api/chat/chatDocumentDelete';
import { useCreateNewChat } from '@/hooks';
import { useAzureFileUpload } from '@repo/file-uploader';
import {
  addChatDocument,
  migrateDraftChatState,
  removeChatDocument,
  selectChatDocuments,
  setChatId,
  updateChatDocumentStatus,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { globalToastService } from '@repo/atomic-components';
import { draftChat } from '@/constants';
import { TEMP_LOCAL_DOCUMENT_ID_PREFIX } from '@/constants';
import { FileStatus } from '@/types/file';
import { chatMessagesCache } from '@/hooks/chatMessagesCache';
import { pairCreatedDocuments } from '@/utils';
import { getDocumentFormat, toDocumentSizeMb } from '@/utils/getDocumentFormat';
import { getApiErrorDetailMessage } from '../utils/chat';

/** Batch `upload-complete` calls for finished uploads. */
const BULK_UPLOAD_COMPLETE_INTERVAL_MS = 5000;

export const useChatFilesUpload = (chatId: string | null, caseId?: string) => {
  const dispatch = useAppDispatch();
  const chatDocuments = useAppSelector(selectChatDocuments);
  const { createNewChat } = useCreateNewChat();

  const [triggerPostChatDocumentBulkCreate] = usePostChatDocumentBulkCreateMutation();
  const [triggerPostChatDocumentBulkGetUpload] = usePostChatDocumentBulkGetUploadMutation();
  const [triggerPostChatDocumentBulkUploadComplete] = usePostChatDocumentBulkUploadCompleteMutation();
  const [triggerPostChatDocumentDelete] = usePostChatDocumentDeleteMutation();

  const ensuredChatIdRef = useRef<string | null>(null);

  useEffect(() => {
    ensuredChatIdRef.current = null;
  }, [chatId, caseId]);

  const ensureChatId = useCallback(async (): Promise<string | null> => {
    if (chatId) return chatId;
    if (ensuredChatIdRef.current) return ensuredChatIdRef.current;

    const createdChatId =
      (await createNewChat({
        ...(caseId ? { caseId } : {}),
        navigateToChatPage: false,
      })) ?? null;

    if (createdChatId) {
      ensuredChatIdRef.current = createdChatId;
      dispatch(setChatId({ chatId: createdChatId }));
      dispatch(migrateDraftChatState({ chatId: createdChatId }));
      chatMessagesCache.migrate(draftChat, createdChatId);
    }

    return createdChatId;
  }, [caseId, chatId, createNewChat, dispatch]);

  /** Blob uploads that finished and are waiting to be reported in the next batch. */
  const pendingCompleteIdsRef = useRef<string[]>([]);
  /**
   * Blob uploads still in flight. Mutated synchronously so callbacks firing
   * before the next render still see an accurate count of what is outstanding.
   */
  const activeUploadIdsRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const flushCompletedUploads = useCallback(() => {
    if (pendingCompleteIdsRef.current.length === 0) return;

    const idsToFlush = pendingCompleteIdsRef.current;
    pendingCompleteIdsRef.current = [];

    triggerPostChatDocumentBulkUploadComplete({ chatDocumentIds: idsToFlush })
      .unwrap()
      .then(() => {
        idsToFlush.forEach((documentId) => {
          dispatch(updateChatDocumentStatus({ documentId, status: FileStatus.CONTENT_EXTRACTION_IN_PROGRESS }));
        });
      })
      .catch((error) => {
        console.error(`Failed to complete upload for ${idsToFlush.join(', ')}:`, error);
        const message = getApiErrorDetailMessage(error, 'Failed to complete upload');
        idsToFlush.forEach((documentId) => {
          dispatch(updateChatDocumentStatus({ documentId, status: FileStatus.UPLOAD_FAILED, error: message }));
        });
      });
  }, [dispatch, triggerPostChatDocumentBulkUploadComplete]);

  const stopBatchTimer = useCallback(() => {
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  const startBatchTimer = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setInterval(flushCompletedUploads, BULK_UPLOAD_COMPLETE_INTERVAL_MS);
  }, [flushCompletedUploads]);

  // Report whatever is still queued rather than leaving those documents stuck
  // as "uploading" when the user leaves the page mid-batch.
  useEffect(() => {
    return () => {
      stopBatchTimer();
      flushCompletedUploads();
    };
  }, [flushCompletedUploads, stopBatchTimer]);

  const settleUpload = useCallback(
    (documentId: string) => {
      activeUploadIdsRef.current.delete(documentId);
      if (activeUploadIdsRef.current.size > 0) return;

      // Nothing left to wait for, so report the batch now instead of on the next tick.
      stopBatchTimer();
      flushCompletedUploads();
    },
    [flushCompletedUploads, stopBatchTimer],
  );

  const { uploadFile, deleteUpload } = useAzureFileUpload({
    onUploadComplete: (documentId: string) => {
      // The manager has no more work for this file; dropping it here also keeps
      // the source File out of memory while the batch waits to be flushed.
      deleteUpload(documentId);
      pendingCompleteIdsRef.current.push(documentId);
      settleUpload(documentId);
    },
    onUploadError: (documentId: string, error: Error) => {
      console.error(`Failed to upload chat document ${documentId}:`, error);
      deleteUpload(documentId);
      dispatch(
        updateChatDocumentStatus({
          documentId,
          status: FileStatus.UPLOAD_FAILED,
          error: error.message || 'Failed to upload file',
        }),
      );
      settleUpload(documentId);
    },
  });

  const onFilesStartUpload = async (selectedFiles: File[]) => {
    const ensuredChatId = await ensureChatId();
    if (!ensuredChatId) {
      globalToastService.show({
        closable: true,
        severity: 'error',
        summary: 'Upload failed',
        detail: 'Could not create a chat for file upload. Please try again.',
        life: 6000,
      });
      return;
    }

    let paired: { file: File; documentId: string }[] = [];
    let createError: string | null = null;

    try {
      const { documents } = await triggerPostChatDocumentBulkCreate({
        chatId: ensuredChatId,
        documents: selectedFiles.map((selectedFile) => ({
          documentName: selectedFile.name,
          documentSizeMb: toDocumentSizeMb(selectedFile.size),
          documentFormat: getDocumentFormat(selectedFile.name),
        })),
      }).unwrap();

      paired = pairCreatedDocuments(selectedFiles, documents);
    } catch (error) {
      console.error('Failed to create chat documents:', error);
      createError = getApiErrorDetailMessage(error, 'Failed to create document');
    }

    let uploadsByDocumentId = new Map<string, { uploadPath: string; uploadToken: string }>();
    let getUploadError: string | null = null;

    if (paired.length > 0) {
      try {
        const { uploads } = await triggerPostChatDocumentBulkGetUpload({
          chatDocumentIds: paired.map(({ documentId }) => documentId),
        }).unwrap();

        uploadsByDocumentId = new Map(
          uploads.map(({ chatDocumentId, uploadPath, uploadToken }) => [chatDocumentId, { uploadPath, uploadToken }]),
        );
      } catch (error) {
        console.error('Failed to get upload information for chat documents:', error);
        getUploadError = getApiErrorDetailMessage(error, 'Failed to get upload information');
      }
    }

    const pairedFiles = new Set(paired.map(({ file }) => file));
    const uploadFileInfos = [
      ...paired.map(({ file, documentId }) => {
        const upload = uploadsByDocumentId.get(documentId);
        return {
          file,
          documentId,
          uploadPath: upload?.uploadPath ?? null,
          uploadToken: upload?.uploadToken ?? null,
          error: getUploadError,
        };
      }),
      // Files the bulk create never came back with — either it failed outright or
      // it returned fewer documents than we asked for.
      ...selectedFiles
        .filter((file) => !pairedFiles.has(file))
        .map((file) => ({
          file,
          documentId: null,
          uploadPath: null,
          uploadToken: null,
          error: createError ?? 'Failed to create document',
        })),
    ];

    const handleDocumentWithId = (
      documentId: string,
      file: File,
      uploadPath: string | null,
      uploadToken: string | null,
      error: string | null,
    ) => {
      dispatch(
        addChatDocument({
          documentId,
          documentName: file.name,
          chatId: ensuredChatId,
        }),
      );

      if (error) {
        dispatch(
          updateChatDocumentStatus({
            documentId,
            status: FileStatus.UPLOAD_FAILED,
            error,
          }),
        );
        return;
      }

      if (uploadPath && uploadToken) {
        activeUploadIdsRef.current.add(documentId);
        startBatchTimer();
        uploadFile(documentId, file, uploadPath, uploadToken);
        return;
      }

      dispatch(
        updateChatDocumentStatus({
          documentId,
          status: FileStatus.UPLOAD_FAILED,
          error: 'Failed to get upload information',
        }),
      );
    };

    const handleDocumentWithoutId = (file: File, error: string | null) => {
      const tempId = `${TEMP_LOCAL_DOCUMENT_ID_PREFIX}${Date.now()}-${Math.random()}`;
      dispatch(
        addChatDocument({
          documentId: tempId,
          documentName: file.name,
          chatId: ensuredChatId,
        }),
      );
      dispatch(
        updateChatDocumentStatus({
          documentId: tempId,
          status: FileStatus.UPLOAD_FAILED,
          error: error || 'Failed to create document',
        }),
      );
    };

    uploadFileInfos.forEach(({ file, documentId, uploadPath, uploadToken, error }) => {
      if (documentId) {
        handleDocumentWithId(documentId, file, uploadPath, uploadToken, error);
      } else {
        handleDocumentWithoutId(file, error);
      }
    });
  };

  const deleteChatDocument = async (documentId: string) => {
    try {
      if (documentId.includes(TEMP_LOCAL_DOCUMENT_ID_PREFIX)) {
        dispatch(removeChatDocument(documentId));
        return;
      }
      await triggerPostChatDocumentDelete({ chatDocumentId: documentId }).unwrap();
      dispatch(removeChatDocument(documentId));
    } catch (error) {
      globalToastService.show({
        closable: true,
        severity: 'error',
        summary: 'Failed to delete file',
        detail: 'Something went wrong while deleting the file. Please try again.',
        life: 6000,
      });
      console.error('Failed to delete chat document:', error);
    }
  };

  return {
    onFilesStartUpload,
    deleteChatDocument,
    chatDocuments,
  };
};
